/**
 * --------------------------------------------------------------------------
 * CoreUI PRO data-grid ui/tooltip.ts
 * License (https://coreui.io/pro/license/)
 * --------------------------------------------------------------------------
 */

const CLASS_NAME_TOOLTIP = 'data-grid-tooltip'
const ATTRIBUTE = 'data-coreui-tooltip'
const SELECTOR = `[${ATTRIBUTE}]`
const SHOW_DELAY = 200
const GAP = 6
const EDGE = 4

export interface GridTooltip {
  dispose: () => void
}

// A single shared, delegated tooltip for grid-owned icon controls. Positioned
// with position: fixed so it escapes the viewport's overflow, above the anchor
// with a flip below when there isn't room. Purely visual (aria-hidden) - the
// anchors already carry their accessible name via aria-label / hidden text.
export const createTooltip = (root: HTMLElement): GridTooltip => {
  const element = document.createElement('div')
  element.className = CLASS_NAME_TOOLTIP
  element.setAttribute('role', 'tooltip')
  element.setAttribute('aria-hidden', 'true')
  element.hidden = true
  root.append(element)

  let anchor: HTMLElement | null = null
  let showTimer: ReturnType<typeof setTimeout> | undefined

  const anchorFrom = (target: EventTarget | null): HTMLElement | null => {
    const element_ = target instanceof Element ? target.closest(SELECTOR) : null
    return element_ instanceof HTMLElement && element_.getAttribute(ATTRIBUTE) ? element_ : null
  }

  const position = (): void => {
    if (!anchor) {
      return
    }

    const rect = anchor.getBoundingClientRect()
    const width = element.offsetWidth
    const height = element.offsetHeight
    const above = rect.top - height - GAP
    const top = above >= 0 ? above : rect.bottom + GAP
    const left = Math.max(EDGE, Math.min(rect.left + ((rect.width - width) / 2), window.innerWidth - width - EDGE))
    element.style.top = `${top}px`
    element.style.left = `${left}px`
    element.style.visibility = 'visible'
  }

  const show = (target: HTMLElement): void => {
    anchor = target
    element.textContent = target.getAttribute(ATTRIBUTE)
    element.style.visibility = 'hidden'
    element.hidden = false
    position()
  }

  const hide = (): void => {
    clearTimeout(showTimer)
    anchor = null
    element.hidden = true
  }

  const onPointerOver = (event: PointerEvent): void => {
    const target = anchorFrom(event.target)
    if (!target || target === anchor) {
      return
    }

    clearTimeout(showTimer)
    showTimer = setTimeout(() => show(target), SHOW_DELAY)
  }

  const onPointerOut = (event: PointerEvent): void => {
    if (anchor && event.relatedTarget instanceof Node && anchor.contains(event.relatedTarget)) {
      return
    }

    hide()
  }

  const onFocusIn = (event: FocusEvent): void => {
    const target = anchorFrom(event.target)
    if (target) {
      show(target)
    }
  }

  root.addEventListener('pointerover', onPointerOver as EventListener)
  root.addEventListener('pointerout', onPointerOut as EventListener)
  root.addEventListener('pointerdown', hide)
  root.addEventListener('focusin', onFocusIn as EventListener)
  root.addEventListener('focusout', hide)
  window.addEventListener('scroll', hide, true)

  return {
    dispose() {
      clearTimeout(showTimer)
      root.removeEventListener('pointerover', onPointerOver as EventListener)
      root.removeEventListener('pointerout', onPointerOut as EventListener)
      root.removeEventListener('pointerdown', hide)
      root.removeEventListener('focusin', onFocusIn as EventListener)
      root.removeEventListener('focusout', hide)
      window.removeEventListener('scroll', hide, true)
      element.remove()
    }
  }
}
